import type { Epic } from 'redux-observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { Observable } from 'rxjs/Observable';
import { logSafeErrorWithoutCustomerData } from '@atlassian/jira-common-directory-v2/src/utils/log-safe-error/index.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { VERSIONS_ERROR_ANALYTICS } from '../../common/constants.tsx';
import {
	getUnresolvedIssues,
	getVersion as getVersionService,
	releaseVersion as releaseVersionService,
} from '../services/index.tsx';
import {
	INITIAL_REQUEST,
	RELEASE_VERSION_REQUEST,
	initialRequestSuccess,
	releaseVersionSuccess,
	releaseVersionFailure,
	releaseVersionValidationFailure,
	setUnreleasedIssuesCount,
} from '../state/actions.tsx';
import {
	getBaseUrl,
	getVersion as getVersionToRelease,
	isKeepingIssues,
} from '../state/selectors.tsx';
import type { State } from '../state/types.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const initialRequestEpic: Epic<any, State> = (action$, store) =>
	action$.ofType(INITIAL_REQUEST).mergeMap(({ payload: versionId }) => {
		const state = store.getState();
		const baseUrl = getBaseUrl(state);

		return Observable.fromPromise(getVersionService(baseUrl, versionId))
			.mergeMap((version) => Observable.of(initialRequestSuccess(version)))
			.catch((error: FetchError) => {
				logSafeErrorWithoutCustomerData(
					error,
					'project-directories.versions.release.ops.get.version',
					VERSIONS_ERROR_ANALYTICS,
				);
				return Observable.of(releaseVersionFailure({ id: versionId }));
			});
	});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchUnresolvedIssues: Epic<any, State> = (action$, store) =>
	action$.ofType(INITIAL_REQUEST).mergeMap(({ payload: versionId }) => {
		const state = store.getState();
		const baseUrl = getBaseUrl(state);

		return Observable.fromPromise(getUnresolvedIssues(baseUrl, versionId))
			.mergeMap((unresolvedIssuesCount) =>
				Observable.of(setUnreleasedIssuesCount(unresolvedIssuesCount)),
			)
			.catch((error: FetchError) => {
				logSafeErrorWithoutCustomerData(
					error,
					'project-directories.versions.release.ops.get.getUnresolvedIssues',
					VERSIONS_ERROR_ANALYTICS,
				);
				return Observable.empty<never>();
			});
	});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const releaseVersionRequestEpic: Epic<any, State> = (action$, store) =>
	action$.ofType(RELEASE_VERSION_REQUEST).mergeMap(() => {
		const state = store.getState();
		const baseUrl = getBaseUrl(state);
		const keepIssues = isKeepingIssues(state);
		const version = getVersionToRelease(state);
		const versionToRelease = keepIssues ? omit(version, 'moveUnfixedIssuesTo') : version;

		return (
			Observable.fromPromise(releaseVersionService(baseUrl, versionToRelease))
				.mergeMap((releasedVersion) => Observable.of(releaseVersionSuccess(releasedVersion)))
				// @ts-expect-error - TS2345 - Argument of type '(error: FetchError) => Observable<ReleaseVersionFailureAction> | Observable<ReleaseVersionValidationFailureAction>' is not assignable to parameter of type '(err: any, caught: Observable<ReleaseVersionSuccessAction>) => ObservableInput<ReleaseVersionFailureAction>'.
				.catch((error: FetchError) => {
					try {
						const errors = pick(JSON.parse(error.message).errors, [
							'releaseDate',
							'moveUnfixedIssuesTo',
						]);
						if (!isEmpty(errors)) {
							return Observable.of(releaseVersionValidationFailure(errors));
						}
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
					} catch (e: any) {
						/**/
					}
					logSafeErrorWithoutCustomerData(
						error,
						'project-directories.versions.release.ops.release.version',
						VERSIONS_ERROR_ANALYTICS,
					);
					// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
					return Observable.of(releaseVersionFailure());
				})
		);
	});
